/**
 * Data Primitives
 *
 * Building blocks for displaying data:
 * - Table: Data table with optional sorting
 * - List: Ordered/unordered list with optional icons
 * - Stat: Single metric display
 * - StatRow: Row of metrics
 * - Progress: Progress bar
 * - MiniChart: Simple bar/line visualization
 */

import React from 'react';
import DrillableValue from './DrillableValue';

export function Table({
  columns = [], // [{ key, label, align, width }]
  rows = [], // [{ col1: val1, col2: val2 }]
  compact,
  striped,
  maxRows = 20,
  style = {},
}) {
  const displayRows = rows.slice(0, maxRows);
  const hasMore = rows.length > maxRows;
  const padding = compact ? 'var(--space-1) var(--space-2)' : 'var(--space-2) var(--space-3)';

  return (
    <div
      style={{
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--text-sm)',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--color-surface)' }}>
              {columns.map((col, idx) => (
                <th
                  key={col.key || idx}
                  style={{
                    padding,
                    textAlign: col.align || 'left',
                    fontWeight: 'var(--font-semibold)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    borderBottom: '1px solid var(--color-border)',
                    width: col.width,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label || col.key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                style={{
                  background: striped && rowIdx % 2 === 1 ? 'var(--color-surface)' : 'transparent',
                }}
              >
                {columns.map((col, colIdx) => (
                  <td
                    key={col.key || colIdx}
                    style={{
                      padding,
                      textAlign: col.align || 'left',
                      borderBottom: rowIdx < displayRows.length - 1 ? '1px solid var(--color-border)' : 'none',
                      color: 'var(--color-text)',
                    }}
                  >
                    {renderCell(row[col.key], col.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <div
          style={{
            padding: 'var(--space-2)',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          Showing {maxRows} of {rows.length} rows
        </div>
      )}
    </div>
  );
}

/**
 * Render a cell value. Cells can be plain values or objects carrying
 * provenance: { value|text, drill: { nodeType, nodeValue, department?, ... } }.
 * Drillable cells open the shared transaction drill-down modal on click.
 */
function renderCell(cell, format) {
  if (cell !== null && typeof cell === 'object' && !Array.isArray(cell) && !React.isValidElement(cell)) {
    const display = formatCellValue(cell.value ?? cell.text, format);
    return cell.drill
      ? <DrillableValue drill={cell.drill}>{display}</DrillableValue>
      : display;
  }
  return formatCellValue(cell, format);
}

function formatCellValue(value, format) {
  if (value === null || value === undefined) return '—';

  switch (format) {
    case 'currency':
      return typeof value === 'number'
        ? '$' + value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        : value;
    case 'percent':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : value;
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;
    case 'date':
      return value ? new Date(value).toLocaleDateString() : '—';
    default:
      return value;
  }
}

export function List({
  items = [], // [{ text, icon, badge, color, description }] or ['string']
  ordered,
  compact,
  style = {},
}) {
  const padding = compact ? 'var(--space-1)' : 'var(--space-2)';

  return (
    <ul
      style={{
        listStyle: ordered ? 'decimal' : 'none',
        padding: ordered ? '0 0 0 var(--space-4)' : 0,
        margin: 0,
        ...style,
      }}
    >
      {items.map((item, idx) => {
        const isString = typeof item === 'string';
        const text = isString ? item : item.text;
        const icon = isString ? null : item.icon;
        const badge = isString ? null : item.badge;
        const color = isString ? null : item.color;
        const description = isString ? null : item.description;

        return (
          <li
            key={idx}
            style={{
              display: 'flex',
              alignItems: description ? 'flex-start' : 'center',
              gap: 'var(--space-2)',
              padding,
              borderBottom: idx < items.length - 1 ? '1px solid var(--color-border)' : 'none',
            }}
          >
            {icon && <span style={{ fontSize: '14px' }}>{icon}</span>}
            {!ordered && !icon && (
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: color ? `var(--color-${color})` : 'var(--color-primary)',
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text)',
                }}
              >
                {text}
              </div>
              {description && (
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: '2px',
                  }}
                >
                  {description}
                </div>
              )}
            </div>
            {badge && (
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '2px 6px',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-surface-hover)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {badge}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function Stat({
  label,
  value,
  unit,
  change, // { value, direction: 'up' | 'down' }
  color, // primary, success, warning, error
  icon,
  size = 'md', // sm, md, lg
  drill, // { nodeType, nodeValue, department?, ... } — click value to see transactions
  style = {},
}) {
  const sizes = {
    sm: { value: 'var(--text-lg)', label: 'var(--text-xs)' },
    md: { value: 'var(--text-xl)', label: 'var(--text-xs)' },
    lg: { value: 'var(--text-2xl)', label: 'var(--text-sm)' },
  };

  const s = sizes[size] || sizes.md;

  return (
    <div style={{ textAlign: 'center', ...style }}>
      {icon && <div style={{ fontSize: '20px', marginBottom: 'var(--space-1)' }}>{icon}</div>}
      <div
        style={{
          fontSize: s.value,
          fontWeight: 'var(--font-bold)',
          color: color ? `var(--color-${color})` : 'var(--color-text)',
          lineHeight: 1.2,
        }}
      >
        <DrillableValue drill={drill}>{formatStatValue(value)}</DrillableValue>
        {unit && (
          <span
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-normal)',
              color: 'var(--color-text-muted)',
              marginLeft: '2px',
            }}
          >
            {unit}
          </span>
        )}
      </div>
      {label && (
        <div
          style={{
            fontSize: s.label,
            color: 'var(--color-text-muted)',
            marginTop: '4px',
          }}
        >
          {label}
        </div>
      )}
      {change && (
        <div
          style={{
            fontSize: 'var(--text-xs)',
            color: change.direction === 'up' ? 'var(--color-success)' : 'var(--color-error)',
            marginTop: '4px',
          }}
        >
          {change.direction === 'up' ? '↑' : '↓'} {change.value}
        </div>
      )}
    </div>
  );
}

function formatStatValue(value) {
  // Numeric strings (Postgres numeric columns) format like numbers;
  // genuine text ("86.1%", "13d") passes through untouched.
  const n = typeof value === 'number' ? value
    : (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value)) ? Number(value) : null);
  if (n === null) return value;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function StatRow({ stats = [], style = {} }) {
  return (
    <div
      style={{
        display: 'grid',
        // auto-fit keeps stats from overflowing at mobile widths (390px)
        gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        ...style,
      }}
    >
      {stats.map((stat, idx) => (
        <Stat key={idx} size="sm" {...stat} />
      ))}
    </div>
  );
}

export function Progress({
  value, // 0-100 or 0-1
  max = 100,
  label,
  showValue,
  color = 'primary',
  size = 'md', // sm, md, lg
  style = {},
}) {
  const pct = max === 1 ? value * 100 : (value / max) * 100;
  const heights = { sm: '4px', md: '8px', lg: '12px' };

  return (
    <div style={style}>
      {(label || showValue) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--text-xs)',
            marginBottom: 'var(--space-1)',
          }}
        >
          <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
          {showValue && <span style={{ fontWeight: 'var(--font-medium)' }}>{Math.round(pct)}%</span>}
        </div>
      )}
      <div
        style={{
          height: heights[size],
          borderRadius: 'var(--radius-full)',
          background: 'var(--color-surface-hover)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(pct, 100)}%`,
            height: '100%',
            borderRadius: 'var(--radius-full)',
            background: `var(--color-${color})`,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

export function MiniChart({
  type = 'bar', // bar, line
  data = [], // [{ label, value, color }] or [number]
  height = 60,
  showLabels,
  style = {},
}) {
  if (data.length === 0) return null;

  // Coerce: backend numerics arrive as strings; string math → NaN bar heights
  const values = data.map(d => Number(typeof d === 'object' ? d?.value : d) || 0);
  const maxValue = Math.max(...values);

  if (type === 'bar') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '4px',
          height,
          ...style,
        }}
      >
        {data.map((item, idx) => {
          const value = values[idx];
          const label = typeof item === 'object' ? item?.label : null;
          const color = (typeof item === 'object' && item?.color) || 'primary';
          const barHeight = maxValue > 0 ? (value / maxValue) * 100 : 0;

          return (
            <div
              key={idx}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: `${barHeight}%`,
                  minHeight: '2px',
                  background: `var(--color-${color})`,
                  borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                  transition: 'height 0.3s ease',
                }}
              />
              {showLabels && label && (
                <div
                  style={{
                    fontSize: '9px',
                    color: 'var(--color-text-muted)',
                    marginTop: '4px',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Line chart (simplified)
  return (
    <div
      style={{
        height,
        position: 'relative',
        ...style,
      }}
    >
      <svg width="100%" height="100%" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="2"
          points={values
            .map((v, i) => {
              const x = (i / (values.length - 1)) * 100;
              const y = 100 - (maxValue > 0 ? (v / maxValue) * 100 : 0);
              return `${x}%,${y}%`;
            })
            .join(' ')}
        />
      </svg>
    </div>
  );
}
